import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []); // handleScroll is defined inside useEffect, no dependency needed

  const scrollToSection = (id) => {
    // If not on home page, navigate to home first
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navigateToPage = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-md"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div
            className="flex items-center cursor-pointer group"
            onClick={() => navigateToPage("/")}
            data-testid="header-logo"
          >
            <div className="relative mr-3">
              <svg
                viewBox="0 0 64 64"
                className="w-11 h-11 md:w-12 md:h-12"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="bj-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3da5ba" />
                    <stop offset="100%" stopColor="#1a7a8a" />
                  </linearGradient>
                </defs>
                <circle cx="32" cy="32" r="30" fill="url(#bj-grad)" />
                <path
                  d="M14 38 C 22 30, 30 46, 38 38 S 54 30, 50 42"
                  stroke="white"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
                <text
                  x="32"
                  y="26"
                  textAnchor="middle"
                  fontFamily="Arial, sans-serif"
                  fontWeight="800"
                  fontSize="16"
                  fill="white"
                  letterSpacing="0.5"
                >
                  BJ
                </text>
              </svg>
            </div>
            <div className="flex flex-col leading-none">
              <span
                className={`text-2xl md:text-3xl font-extrabold tracking-tight transition-colors duration-300 ${
                  isScrolled ? "text-[#1a7a8a]" : "text-white"
                }`}
              >
                Basic <span className="text-[#3da5ba]">Job</span>
              </span>
              <span
                className={`text-[10px] md:text-xs uppercase tracking-[0.25em] mt-1 transition-colors duration-300 ${
                  isScrolled ? "text-gray-500" : "text-gray-200"
                }`}
              >
                Renovations
              </span>
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("services")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection("appointments")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.book')}
            </button>
            <button
              onClick={() => scrollToSection("projects")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.projects')}
            </button>
            <button
              onClick={() => scrollToSection("testimonials")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.testimonials')}
            </button>
            <button
              onClick={() => navigateToPage("/sustainability")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.sustainability')}
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className={`text-sm font-medium transition-colors hover:text-[#1a7a8a] ${
                isScrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {t('nav.contact')}
            </button>
            <LanguageSwitcher />
          </nav>

          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? "text-gray-900" : "text-white"} size={24} />
            ) : (
              <Menu className={isScrolled ? "text-gray-900" : "text-white"} size={24} />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white shadow-lg rounded-lg mt-2">
            <nav className="flex flex-col space-y-4 px-4">
              <button
                onClick={() => scrollToSection("services")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => scrollToSection("appointments")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.book')}
              </button>
              <button
                onClick={() => scrollToSection("projects")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.projects')}
              </button>
              <button
                onClick={() => scrollToSection("testimonials")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.testimonials')}
              </button>
              <button
                onClick={() => navigateToPage("/sustainability")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.sustainability')}
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-left text-gray-700 hover:text-[#1a7a8a] font-medium"
              >
                {t('nav.contact')}
              </button>
              <div className="pt-4 border-t border-gray-200">
                <LanguageSwitcher />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
